import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Keyboard, Platform, ScrollView, TextInput, View } from 'react-native';
import { component } from '../../Decorators/Component';
import { CoreConfig } from '../../../CoreConfig';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { IconComponent } from '../Icons/IconComponent';
import { SearchItemView } from './SearchItemView';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { DenseButtonView } from '../Buttons/DenseButtonView';
import { debounce } from '../../../Helpers/Debounce/Debounce';
import { waitTimeout } from '../../../Helpers/Background/Background';
import { useAnimation } from '../Animations/AnimationHooks';
import { getDirection, useDirection } from '../../Hooks/UseDirection';
const CenterPosition = 16;
const TopPosition = -8;
const sterialize = (value, getItem) => {
    if (value === undefined || value === null || !getItem) {
        return '';
    }
    let new_text = getItem(value);
    if (typeof new_text !== 'string' && typeof new_text !== 'number') {
        new_text = '';
    }
    if (typeof new_text === 'number') {
        new_text = new_text.toString();
    }
    return new_text;
};
export const SearchView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.search,
    style(theme, mediaQuery, window, props) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const left = ((_a = props.containerStyle) === null || _a === void 0 ? void 0 : _a.paddingLeft) || ((_b = props.containerStyle) === null || _b === void 0 ? void 0 : _b.paddingStart) || ((_c = props.containerStyle) === null || _c === void 0 ? void 0 : _c.paddingHorizontal) || ((_d = props.containerStyle) === null || _d === void 0 ? void 0 : _d.padding) || 0;
        const right = ((_e = props.containerStyle) === null || _e === void 0 ? void 0 : _e.paddingRight) || ((_f = props.containerStyle) === null || _f === void 0 ? void 0 : _f.paddingEnd) || ((_g = props.containerStyle) === null || _g === void 0 ? void 0 : _g.paddingHorizontal) || ((_h = props.containerStyle) === null || _h === void 0 ? void 0 : _h.padding) || 0;
        const top = ((_j = props.containerStyle) === null || _j === void 0 ? void 0 : _j.paddingTop) || ((_k = props.containerStyle) === null || _k === void 0 ? void 0 : _k.paddingVertical) || ((_l = props.containerStyle) === null || _l === void 0 ? void 0 : _l.padding) || 0;
        return {
            container: Object.assign({ width: '100%' }, props.containerStyle),
            button: {
                marginTop: -4
            },
            scrollView: Object.assign({ backgroundColor: '#fff', maxHeight: 200, overflow: 'hidden', borderWidth: 1, borderRadius: 8, borderStyle: 'solid', borderColor: ((_m = theme.colors) === null || _m === void 0 ? void 0 : _m.secondary_color) || '#115AC9' }, Platform.select({
                android: {
                    minHeight: 60,
                    marginTop: 5,
                    marginHorizontal: 4,
                },
                default: Object.assign(Object.assign({ position: 'absolute' }, props.showAboveResults
                    ? {
                        bottom: 52
                    }
                    : {
                        top: 52 + top,
                    }), { left: 4 + left, right: 4 + right })
            })),
            noItems: {
                padding: 20,
                textAlign: 'center',
                fontSize: 16,
                lineHeight: 24
            },
            label: {
                borderRadius: 3,
                overflow: 'hidden',
                zIndex: 1,
                lineHeight: 18,
                fontSize: 16,
                color: 'rgba(115, 135, 159, 1)',
                paddingHorizontal: 2,
                backgroundColor: '#fff',
                position: 'absolute'
            },
            textInput: {
                backgroundColor: '#fff',
                borderRadius: 8,
                minHeight: 48,
                flex: 1,
                zIndex: 2,
                minWidth: 0,
                height: '100%',
                paddingVertical: 0,
                paddingHorizontal: 15,
                fontSize: 16,
                maxHeight: 18,
                lineHeight: 18,
                color: 'rgb(115, 135, 159)',
                borderBottomWidth: 0,
                borderWidth: 0
            },
            textInputHovered: {
                color: '#394E66'
            },
            textInputFocused: {
                color: '#394E66'
            },
            textInputDisabled: {
                color: '#BFCCDA'
            },
            inputContainer: {
                flexDirection: 'row',
                alignItems: 'center',
                borderRadius: 8,
                borderColor: ((_o = theme.colors) === null || _o === void 0 ? void 0 : _o.link_color) || 'rgb(191, 204, 218)',
                borderWidth: 1,
                borderStyle: 'solid',
                marginVertical: 1,
                marginHorizontal: 3
            },
            inputContainerFocused: {
                borderColor: ((_p = theme.colors) === null || _p === void 0 ? void 0 : _p.secondary_color) || 'rgb(17, 90, 201)'
            },
            clearBtn: {
                marginTop: Platform.select({
                    web: -4
                }),
                paddingHorizontal: 15,
                flex: undefined
            },
            after: Object.assign({ flexDirection: 'row', alignItems: 'center', height: '100%', borderStyle: 'solid', padding: 15, backgroundColor: '#F0F3F7' }, getDirection().value === 'rtl' && Platform.OS === 'web'
                ? {
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    borderRightWidth: 1,
                }
                : {
                    borderLeftWidth: 1,
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8
                }),
        };
    }
})(({ style, value, disabled, bottom, hideNoItemsPlaceholder, minSearchLength, clear, onClear, removeAfter, localization, zIndex, onSearch, onRef, getItem, getKey, label, placeholder, onSelect, onBlur, onFocus }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const inputRef = useRef();
    const focusTime = useRef(0);
    const focusRef = useRef(false);
    const valueRef = useRef(undefined);
    const [animation, setAnimation] = useAnimation({ initialValue: 0 });
    const [textAnimatedColor, setTextAnimatedColor] = useAnimation({ initialValue: 0, duration: 100, nativeDriver: false });
    const [scale, setScale] = useAnimation({ initialValue: (value || placeholder) && label ? 0.9 : 1, duration: 200, nativeDriver: false });
    const [color, setColor] = useAnimation({ initialValue: 0, duration: 100, nativeDriver: false });
    const [top, setTop] = useAnimation({ initialValue: (value || placeholder) ? TopPosition : CenterPosition, duration: 200, nativeDriver: false });
    const [dir] = useDirection();
    const [text, setText] = useState(sterialize(value, getItem));
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(false);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(0);
    const [end, setEnd] = useState(false);
    const [labelLeft, setLabelLeft] = useState(15);
    const search = useCallback(debounce((value) => {
        if (minSearchLength && value.length < minSearchLength) {
            return;
        }
        setLoading(true);
        return Promise.resolve()
            .then(() => onSearch === null || onSearch === void 0 ? void 0 : onSearch(value, 1))
            .then(({ isEnd, page, values }) => {
            setLoading(false);
            setEnd(!!isEnd);
            setPage(page);
            setResults(values);
        })
            .catch(error => setLoading(false));
    }), [onSearch, minSearchLength]);
    const handleRef = useCallback((ref) => {
        inputRef.current = ref;
        onRef === null || onRef === void 0 ? void 0 : onRef({
            focus: () => ref.focus(),
            blur: () => ref.blur(),
            setValue: (value) => {
                setText(value);
                return value;
            }
        });
    }, [onRef]);
    const handleSelect = useCallback((value) => {
        valueRef.current = value;
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(value);
        waitTimeout(50).then(() => Keyboard.dismiss());
    }, [onSelect]);
    const handleManualFocus = useCallback(() => {
        var _a;
        if (disabled) {
            return;
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [disabled]);
    const handleFocus = useCallback(() => {
        var _a;
        if (disabled) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            return;
        }
        focusTime.current = new Date().getTime();
        valueRef.current = undefined;
        setColor(1);
        setTextAnimatedColor(1);
        setTop(TopPosition);
        setScale(0.9);
        setFocus(true);
        focusRef.current = true;
        setVisible(true);
        setAnimation(1);
        setText('');
        search('');
        onFocus === null || onFocus === void 0 ? void 0 : onFocus();
    }, [disabled, search]);
    const handleBlur = useCallback(() => {
        var _a;
        const time = new Date().getTime();
        const delta = time - (focusTime.current || 0);
        const delay = 300;
        if (delta < delay) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        setFocus(false);
        focusRef.current = false;
        setAnimation(0).then(() => setVisible(false));
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
        setColor(0);
        setTextAnimatedColor(0);
        waitTimeout(200).then(() => {
            if (valueRef.current === null) {
                return;
            }
            const new_value = valueRef.current || value;
            const text = sterialize(new_value, getItem);
            setText(text);
            if (text.length > 0 || placeholder) {
                setTop(TopPosition);
                setScale(0.9);
            }
            else {
                setTop(CenterPosition);
                setScale(1);
            }
        });
    }, [placeholder, value, getItem]);
    const handleHover = useCallback(() => {
        if (disabled) {
            return;
        }
        setHover(true);
        setColor(1);
    }, [disabled]);
    const handleHoverOut = useCallback(() => {
        if (disabled) {
            return;
        }
        setHover(false);
        !focus && setColor(0);
        !focus && setTextAnimatedColor(0);
    }, [disabled, focus]);
    const handleChangeText = useCallback((text) => {
        if (disabled) {
            return;
        }
        setText(text);
        search(text);
    }, [disabled]);
    const handleClear = useCallback(() => {
        if (disabled) {
            return;
        }
        setText('');
        onClear === null || onClear === void 0 ? void 0 : onClear();
        waitTimeout(50).then(() => handleManualFocus());
    }, [disabled, handleChangeText, handleManualFocus, onClear]);
    useEffect(() => {
        if (focusRef.current) {
            return;
        }
        valueRef.current = null;
        const new_text = sterialize(value, getItem);
        if (new_text.length > 0 || placeholder) {
            setTop(TopPosition);
            setScale(0.9);
        }
        else {
            setTop(CenterPosition);
            setScale(1);
        }
        setText(new_text);
    }, [value, getItem, placeholder]);
    const labelStyles = [
        style === null || style === void 0 ? void 0 : style.label,
        Object.assign(Object.assign({}, dir === 'rtl' && Platform.OS === 'web'
            ? { right: labelLeft }
            : { left: labelLeft }), { color: textAnimatedColor.interpolate({
                inputRange: [0, 1],
                outputRange: [(_a = style === null || style === void 0 ? void 0 : style.label) === null || _a === void 0 ? void 0 : _a.color, (_b = style === null || style === void 0 ? void 0 : style.inputContainerFocused) === null || _b === void 0 ? void 0 : _b.borderColor]
            }), transform: [
                { translateY: top },
                { scale }
            ] })
    ];
    let placeholderColor = (_c = style === null || style === void 0 ? void 0 : style.textInput) === null || _c === void 0 ? void 0 : _c.color;
    if (disabled) {
        placeholderColor = (_d = style === null || style === void 0 ? void 0 : style.textInputDisabled) === null || _d === void 0 ? void 0 : _d.color;
        labelStyles.push({
            color: (_e = style === null || style === void 0 ? void 0 : style.textInputDisabled) === null || _e === void 0 ? void 0 : _e.color
        });
    }
    const borderColor = color.interpolate({
        inputRange: [0, 1],
        outputRange: [(_f = style === null || style === void 0 ? void 0 : style.inputContainer) === null || _f === void 0 ? void 0 : _f.borderColor, (_g = style === null || style === void 0 ? void 0 : style.inputContainerFocused) === null || _g === void 0 ? void 0 : _g.borderColor]
    });
    const inputContainerStyle = Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.inputContainer), Platform.select({
        default: {
            borderColor,
            backgroundColor: '#fff'
        },
        ios: Object.assign({ borderColor, backgroundColor: '#fff' }, focus && {
            shadowColor: borderColor,
            shadowOpacity: 1,
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowRadius: 1
        }),
        web: Object.assign({ borderColor, backgroundColor: '#fff' }, focus && {
            shadowColor: borderColor,
            shadowOpacity: 1,
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowRadius: 2
        })
    }));
    const inputStyles = [
        style === null || style === void 0 ? void 0 : style.textInput,
        hover && !focus && (style === null || style === void 0 ? void 0 : style.textInputHovered),
        (focus || text !== '') && (style === null || style === void 0 ? void 0 : style.textInputFocused),
        disabled && (style === null || style === void 0 ? void 0 : style.textInputDisabled),
        { shadowOpacity: 0, shadowRadius: 0 },
        dir === 'rtl' && { direction: 'rtl', textAlign: 'right' },
    ];
    if (Platform.OS === 'web') {
        inputStyles.push({ outlineStyle: 'none', backgroundClip: 'text' });
    }
    return (_jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.container, visible && { zIndex: zIndex || 1 }], children: [_jsxs(Animated.View, { children: [!!label && (_jsx(AnimatedTextView, { textStyle: labelStyles, onPress: handleManualFocus, onMouseEnter: handleHover,
                        onMouseLeave: handleHoverOut, children: label })), _jsxs(AnimatedPressable, { style: inputContainerStyle, onPress: handleManualFocus, children: [_jsx(TextInput, Object.assign({}, {
                                dir,
                                onMouseEnter: handleHover,
                                onMouseLeave: handleHoverOut,
                            }, { ref: handleRef, editable: !disabled, autoCapitalize: 'none', autoCorrect: false, allowFontScaling: false, caretHidden: false, keyboardType: 'default', placeholder: placeholder, placeholderTextColor: placeholderColor, value: text, style: inputStyles, onChangeText: handleChangeText, onFocus: handleFocus, onBlur: handleBlur })), !!clear && !!text && !disabled && (_jsx(DenseButtonView, { hitSlop: 5, wrapperStyle: style === null || style === void 0 ? void 0 : style.clearBtn, containerStyle: { width: undefined, flex: undefined, minHeight: 48 }, onPress: handleClear, children: _jsx(IconComponent, { name: 'si-close-cancel', size: 10, color: '#63778F' }) })), !removeAfter && (_jsx(AnimatedPressable, { style: [
                                    style === null || style === void 0 ? void 0 : style.after,
                                    { borderColor }
                                ], onPress: handleManualFocus, children: _jsx(IconComponent, { name: visible ? 'si-arrow-up' : 'si-arrow-down', size: 8, color: '#63778F' }) }))] })] }), visible && (_jsx(Animated.View, { style: [style === null || style === void 0 ? void 0 : style.scrollView, { opacity: animation }], children: _jsxs(ScrollView, { style: Platform.OS === 'web' && { flex: 1 }, keyboardShouldPersistTaps: 'handled', nestedScrollEnabled: true, children: [results.map((item, index) => (_jsx(SearchItemView, { value: item, getItem: getItem, onPress: handleSelect }, getKey(item, index)))), !hideNoItemsPlaceholder && results.length <= 0 && !loading && (_jsx(SearchItemView, { value: (localization === null || localization === void 0 ? void 0 : localization.no_items) || (localization === null || localization === void 0 ? void 0 : localization.no_results_found) || 'No results found!', getItem: value => value })), !loading && bottom && bottom(), loading && (_jsx(View, { style: style === null || style === void 0 ? void 0 : style.noItems, children: _jsx(ActivityIndicator, { size: 'small' }) }))] }) }))] }));
});
